import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, AlertTriangle, Loader } from 'lucide-react';
import API_BASE_URL from '../config/api';

interface ScannedForm {
  id: string;
  thumbnailPath: string;
  detectedFirstName: string;
  detectedLastName: string;
  nameConfidence: number;
  matchedStudentId: number | null;
  status: string;
  scannedAt: string;
}

const BatchValidationPage: React.FC = () => {
  const navigate = useNavigate();
  const { testId } = useParams<{ testId: string }>();

  const [scannedForms, setScannedForms] = useState<ScannedForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchScannedForms();
  }, [testId]);

  const fetchScannedForms = async () => {
    const token = localStorage.getItem('authToken');

    try {
      const response = await fetch(`${API_BASE_URL}/api/photo-ocr/scanned-forms/${testId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch');

      const data = await response.json();
      setScannedForms(data.scannedForms || []);
    } catch (err) {
      console.error('Erreur chargement scans:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessAll = async () => {
    setProcessing(true);
    const token = localStorage.getItem('authToken');

    try {
      const response = await fetch(`${API_BASE_URL}/api/photo-ocr/process-batch`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ testId })
      });

      if (!response.ok) throw new Error('Processing failed');

      const data = await response.json();

      if (data.success) {
        fetchScannedForms(); // Rafraîchir
      }
    } catch (err) {
      console.error('Erreur traitement batch:', err);
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'processed':
        return <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-800">Traité</span>;
      case 'pending':
        return <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-800">En attente</span>;
      case 'processing':
        return <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">En cours...</span>;
      case 'error':
        return <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-800">Erreur</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const getConfidenceBadge = (confidence: number) => {
    if (confidence >= 70) {
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    } else if (confidence >= 50) {
      return <AlertTriangle className="h-5 w-5 text-orange-500" />;
    } else {
      return <AlertTriangle className="h-5 w-5 text-red-500" />;
    }
  };

  const pendingCount = scannedForms.filter(f => f.status === 'pending').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin mx-auto text-blue-600" />
          <p className="mt-2 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(`/test-results/${testId}`)}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Retour
            </button>

            <h1 className="text-xl font-semibold text-gray-900">
              Validation des Scans
            </h1>

            <button
              onClick={handleProcessAll}
              disabled={processing || pendingCount === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {processing && <Loader className="h-4 w-4 animate-spin" />}
              {processing ? 'Traitement...' : `Traiter Tout (${pendingCount})`}
            </button>
          </div>
        </div>
      </div>

      {/* Statistiques */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Total</p>
            <p className="text-2xl font-bold text-gray-900">{scannedForms.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">En attente</p>
            <p className="text-2xl font-bold text-orange-600">{pendingCount}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Traités</p>
            <p className="text-2xl font-bold text-green-600">
              {scannedForms.filter(f => f.status === 'processed').length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Erreurs</p>
            <p className="text-2xl font-bold text-red-600">
              {scannedForms.filter(f => f.status === 'error').length}
            </p>
          </div>
        </div>

        {/* Liste des scans */}
        {scannedForms.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-500 text-lg">Aucun formulaire scanné pour le moment.</p>
            <button
              onClick={() => navigate(`/camera-scanner/${testId}`)}
              className="mt-4 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Commencer à scanner
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {scannedForms.map((form) => (
              <div key={form.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
                {/* Miniature */}
                <div className="relative">
                  <img
                    src={`${API_BASE_URL}/${form.thumbnailPath}`}
                    alt="Scan"
                    className="w-full h-48 object-cover rounded-t-lg"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>';
                    }}
                  />
                  <div className="absolute top-2 right-2">
                    {getStatusBadge(form.status)}
                  </div>
                </div>

                {/* Infos */}
                <div className="p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {form.detectedFirstName || form.detectedLastName ? (
                        <>
                          <p className="font-semibold text-gray-900">
                            {form.detectedFirstName} {form.detectedLastName}
                          </p>
                          <p className="text-sm text-gray-500">
                            Confiance: {form.nameConfidence}%
                          </p>
                        </>
                      ) : (
                        <p className="text-gray-400 italic">Nom non détecté</p>
                      )}
                    </div>

                    <div>
                      {getConfidenceBadge(form.nameConfidence)}
                    </div>
                  </div>

                  <div className="text-xs text-gray-400">
                    Scanné: {new Date(form.scannedAt).toLocaleString('fr-FR')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BatchValidationPage;
